import {FormInstance} from 'antd/es/form';

import {EFormMode} from 'components/form';
import {IProgramSetProgramsType, IProgramSetProgramType} from 'components/form/fields/fields-tree/fields-tree-types';
import {
    fetchEntityMetadata, IFieldMeta, EFieldType, getActionByType, EMetaActionType, selectMetadata, setMetadata,
} from 'entities/metadata';
import {EEntityType} from 'shared/constants/entities';
import {showMessageFromResponse} from 'shared/utils';
import {TAppState, TDispatch} from 'store/config/types';

import {selectContextRawData} from '../../context/context-selector';
import {setData, setFormUsedAdditionalOptionId, setFormUsedAdditionalOptions} from '../data-actions';
import {fetchFormDataFields, fetchReferenceData} from '../data-api';
import {
    IEntityData,
    TEntityValue,
    IFormEntityData,
    TFormEntityDataType,
} from '../data-types';
import {getAdditionalFields} from './utils/actions.utils';

/**
 * Сравнивает по полям метаданные fields из запроса сервера и из приложения,
 * если поле есть в мете сервера, то оно считается приоритетным
 * @param appFields
 * @param fieldsFromRequest
 * @param serverFields
 * @param fieldsMeta
 * @param contextData
 */
export const mapServerFieldMetaToAppFieldMeta = (
    appFields: IFieldMeta[],
    fieldsFromRequest: any,
    serverFields?: IFieldMeta[],
    fieldsMeta?: any,
    contextData?: any,
) => {
    const newAdditionalFields: IFieldMeta[] = [];
    if (fieldsFromRequest) {
        const fieldKeys = Object.keys(appFields[0]);
        const additionalKeys: (string[] | undefined)[] = [];
        let additionalFieldsFromResponse : any = {};
        let fieldArrName : string = '';
        fieldKeys.forEach(field => {
            additionalKeys.push(appFields[0][field as keyof IFieldMeta]?.toString().split('.'));
        });
        additionalKeys.forEach(key => {
            if (key) {
                [fieldArrName] = key;
            }
            key?.shift();
        });
        if (serverFields) {
            additionalFieldsFromResponse = getAdditionalFields(
                // @ts-ignore
                serverFields[fieldArrName],
                additionalKeys,
                fieldKeys,
                contextData,
            );
        }
        return additionalFieldsFromResponse;
    }

    serverFields?.forEach(field => {
        if (appFields.some(item => field.key === item.key)) {
            const appAdditionalField = appFields.find(item => field.key === item.key);
            const newAdditionalData = appAdditionalField
                ? Object.keys(appAdditionalField).reduce(
                    // @ts-ignore
                    (acc, curr) => ({...acc, [curr]: field[curr] || appAdditionalField[curr]}), {},
                )
                : field;
            newAdditionalFields.push({...field, ...newAdditionalData});
        }
    });
    return newAdditionalFields;
};

export const loadAdditionalData = (
    key: string,
    referenceUrl: string,
    additionalReferenceUrl: string,
    docTemplateId?: string,
    menuId?: TEntityValue | any,
    changingFormStructure?: boolean,
    keepVisibleValues?: boolean,
    id?: string | number,
    entityName?: string,
    paramKey?: string,
    form?: FormInstance,
    formMode?: EFormMode,
    lastUsedAdditionalOptionId?: number,
    formData?: IFormEntityData,
) => async (dispatch: TDispatch, getState: () => TAppState) => {
    const contextData = selectContextRawData(getState()) ?? {};
    const referenceParams = {docTemplateId, menuId};
    const urlParam = paramKey ? menuId[paramKey] : '';

    const setAdditionalData = (loading?: boolean, rows?: IEntityData) => {
        dispatch(setFormUsedAdditionalOptions({
            entityName: entityName || '',
            entityType: additionalReferenceUrl,
            loading,
            data: rows,
        }));
    };

    const getNewDataByKeys = (fieldKeys: string[], newData?: TFormEntityDataType, oldData?: TFormEntityDataType) => {
        if (newData && oldData) {
            fieldKeys.forEach((fieldKey: string) => {
                newData[fieldKey] = oldData[fieldKey];
            });
        }
        return newData;
    };

    try {
        if (changingFormStructure && entityName) {
            const response = await fetchEntityMetadata(entityName, EEntityType.FORM);
            if (response.status === 200) {
                const metadata = {
                    ...response.data,
                    isFilterable: !!getActionByType(EMetaActionType.FILTER)(response.data.actions),
                    isSearchable: !!getActionByType(EMetaActionType.SEARCH)(response.data.actions),
                };
                const additionalFields = selectMetadata(
                    entityName, EEntityType.FORM,
                )(getState())?.additionalOptions?.optionsField?.additionalFields || [];

                const fieldsFromRequest = selectMetadata(
                    entityName, EEntityType.FORM,
                )(getState())?.additionalOptions?.optionsField?.isAdditionalFieldsFromRequest;

                const {data} = await fetchFormDataFields(additionalReferenceUrl, id, urlParam);

                if (id) {
                    dispatch(setFormUsedAdditionalOptionId(
                        {entityName, entityNameChild: additionalReferenceUrl, optionId: id},
                    ));
                }

                const fieldsTreeField = metadata?.fields.find(
                    (field: IFieldMeta) => field.type === EFieldType.FIELDS_TREE,
                );
                const isTriggerFieldFromTree = fieldsTreeField && fieldsTreeField?.children
                        && !!fieldsTreeField?.children.find((child: IFieldMeta) => child.key === key);

                if (isTriggerFieldFromTree && formData) {
                    const optionId = id || (data as unknown as IProgramSetProgramType)?.id;
                    if (optionId) {
                        dispatch(setFormUsedAdditionalOptionId(
                            {
                                entityName,
                                entityNameChild: additionalReferenceUrl,
                                optionId,
                                entityType: EEntityType.FORM,
                            },
                        ));
                    }

                    const programSetPrograms = [
                        {
                            program: data as unknown as IProgramSetProgramType,
                            programArgs: (data as unknown as IProgramSetProgramType)?.programArgs,
                        },
                    ];

                    if (formData.data.programSetPrograms && programSetPrograms) {
                        const editedProgramIndex = form?.getFieldsValue()?.programSetPrograms.findIndex(
                            (programSet: any) => programSet?.program?.id === id,
                        );
                        const oldProgramSetPrograms = (
                            formData.data.programSetPrograms as IProgramSetProgramsType[])[editedProgramIndex];
                        const currentProgramSetPrograms = form?.getFieldsValue()?.programSetPrograms[
                            editedProgramIndex];
                        if (oldProgramSetPrograms
                            && currentProgramSetPrograms.program.id !== oldProgramSetPrograms.program.id) {
                            [(
                                formData.data.programSetPrograms as unknown as IProgramSetProgramsType[]
                            )[editedProgramIndex]] = programSetPrograms;
                        } else {
                            (formData.data.programSetPrograms as IProgramSetProgramsType[]).push(programSetPrograms[0]);
                        }
                    }
                    if (!formData.data.programSetPrograms && programSetPrograms) {
                        formData.data.programSetPrograms = programSetPrograms;
                    }

                    dispatch(setData({
                        entityName,
                        entityType: EEntityType.FORM,
                        data: formData,
                    }));
                    return;
                }

                const newAdditionalFields = mapServerFieldMetaToAppFieldMeta(
                    additionalFields,
                    fieldsFromRequest,
                    data,
                    metadata?.fields,
                    contextData,
                );
                dispatch(setMetadata(
                    {
                        entityName,
                        entityType: EEntityType.FORM,
                        metadata: {
                            ...metadata,
                            fields: [...metadata?.fields, ...newAdditionalFields],
                        },
                    },
                ));
                const fieldsMeta = selectMetadata(
                    entityName, EEntityType.FORM,
                )(getState())?.fields;

                const changingFormDataRefKeys = fieldsMeta.filter(
                    field => !!field?.additionalOptions?.optionsField?.isChangingFormStructure,
                ).map(field => field.key);
                const currentData = form?.getFieldsValue(changingFormDataRefKeys);
                const isFormStructureChanging = changingFormDataRefKeys.some(
                    refKey => formData?.initialData[refKey] !== currentData[refKey],
                );

                // reset all form fields except field which is triggering additional data loading
                if (formMode === EFormMode.CREATE || (formMode === EFormMode.EDIT && isFormStructureChanging)) {
                    let newData = formData?.initialData;
                    if (keepVisibleValues) {
                        const mainFieldKeys = metadata?.fields.map(field => field.key);
                        const newAdditionalFieldKeys = newAdditionalFields.map((field: IFieldMeta) => field.key);
                        const visibleFieldKeys = Array.from(new Set([
                            ...changingFormDataRefKeys,
                            ...newAdditionalFieldKeys,
                            ...mainFieldKeys,
                        ]));
                        const oldData = form?.getFieldsValue();
                        newData = getNewDataByKeys(visibleFieldKeys, newData, oldData);
                    } else {
                        newData = getNewDataByKeys(changingFormDataRefKeys, newData, currentData);
                    }
                    if (formData && formData.initialData) {
                        dispatch(setData({
                            entityName,
                            entityType: EEntityType.FORM,
                            data: newData || formData.initialData,
                        }));
                    }
                }
            }
            return;
        }
        if (id && entityName) {
            dispatch(setFormUsedAdditionalOptionId(
                {entityName, entityNameChild: additionalReferenceUrl, optionId: id},
            ));
        }
        const {data: rows} = await fetchReferenceData({
            referenceUrl: additionalReferenceUrl,
            referenceParams,
        });
        setAdditionalData(false, rows);
    } catch (e) {
        if (e.response) {
            setAdditionalData(false, []);
        }
        showMessageFromResponse({response: e.response, isError: true});
        console.error(e);
    }
};
